// Copyright (c) 2020 embyt GmbH. All rights reserved.

import cloneDeep from "lodash.clonedeep";

import {
  ActionGraphCommand,
  ActionType,
  IAction,
  IActionChargerCommand,
  IActionNewChargerData,
  IActionNewReport,
  IActionNewReportData,
  IActionSortTable,
  IActionTrend,
  IActionTypeChargingSessionList,
  IActionTypeConfirmPopup,
  IActionTypeEdit,
  IActionTypeForgotPassword,
  IActionTypeLogin,
  IActionTypeSetPeriod,
  IActionTypeSignUp,
  IActionTypeZoom,
} from "actions";
import { IState } from "states/states";
import { IStateGui, INITIAL_STATE, ModalPanel, IPeriodData, OcppCmdState } from "states/guistates";
import { getGraphBoundaryDates, getPeriodStart, makeCommandSeqNr, setInputField } from "guiutils";
import { isSet } from "utils";
import { DataState, MainPanel, TimePeriod, UserStateAWSError } from "types";
import { IStateRouter } from "states/routerstates";
import { ImportFile } from "graphql/api";
import { DateTime, DurationLike } from "luxon";
import { DeletionStrategy } from "dbtypes";

// uses webpack definitions
declare const IS_CLOUD: boolean;

// this describes the deletion strategy of the trend data table
function getDeletionBoundary(rootState: IState) {
  const deletionStrategy = rootState.site.siteList?.find(
    (curSite) => curSite.id === rootState.cached.selectedSite,
  )?.cloudSettings?.deletionStrategy;
  const deletionDuration: DurationLike | undefined =
    deletionStrategy === DeletionStrategy.SMALL
      ? { months: 6 }
      : deletionStrategy === DeletionStrategy.LARGE
      ? { years: 3 }
      : undefined;
  const deletionBoundary = isSet(deletionDuration)
    ? DateTime.now().minus(deletionDuration).startOf("month")
    : undefined;
  const aggregationDuration: DurationLike | undefined = { years: 2 };
  const aggregationBoundary = DateTime.now().minus(aggregationDuration).startOf("month");
  return { deletionBoundary, aggregationBoundary };
}

function deriveNewGraphStartDate(
  state: IStateGui,
  rootState: IState,
  identifier: "trendPeriod" | "ioPeriod" | "chargingSessionFilter",
  cmd: IActionTypeSetPeriod,
) {
  const period = state[identifier];
  let newXZoom = period.timePeriod;
  let newStartDate = period.startDate;
  let newEndDate = period.endDate;
  const curGraphDates = getGraphBoundaryDates(
    period.startDate,
    period.endDate,
    rootState.site.tzOffset,
    period.timePeriod,
  );

  if (
    [ActionGraphCommand.BACKWARD, ActionGraphCommand.FORWARD].includes(cmd.command) &&
    period.timePeriod !== TimePeriod.ALL &&
    curGraphDates.startDate // this is the case if timePeriod is not all
  ) {
    const customShift =
      isSet(curGraphDates.startDate) && isSet(curGraphDates.endDate)
        ? (curGraphDates.endDate - curGraphDates.startDate) / (60 * 60 * 24)
        : 1;
    const daysToShift =
      period.timePeriod === TimePeriod.DAY
        ? 1
        : period.timePeriod === TimePeriod.WEEK
        ? 7
        : period.timePeriod === TimePeriod.MONTH
        ? 30
        : period.timePeriod === TimePeriod.CUSTOM
        ? customShift
        : 365;

    if (cmd.command === ActionGraphCommand.BACKWARD) {
      // shift only half period to still hit they day even on summer time changing and even in Feb.
      newStartDate = getPeriodStart(
        curGraphDates.startDate - daysToShift * 0.5 * 24 * 3600,
        period.timePeriod,
        curGraphDates.timezoneOffset,
      );
    } else {
      // (cmd.command === ActionGraphCommand.FORWARD)
      // shift even 1.5 periods to still hit they day even on summer time changing and even in 31 day months
      newStartDate = getPeriodStart(
        curGraphDates.startDate + daysToShift * 1.5 * 24 * 3600,
        period.timePeriod,
        curGraphDates.timezoneOffset,
      );
      // check whether we reached the end of the data range
      const endGraphDates = getGraphBoundaryDates(
        undefined,
        undefined,
        rootState.site.tzOffset,
        period.timePeriod,
      );
      if (endGraphDates.startDate && newStartDate >= endGraphDates.startDate) {
        newStartDate = undefined;
      }
    }
  } else if (cmd.command === ActionGraphCommand.SET_PERIOD) {
    newXZoom = cmd.period!;
    if (newXZoom !== TimePeriod.CUSTOM) {
      // keep zoomed date on zoom-in
      if (isSet((period as IPeriodData).zoomMin)) {
        newStartDate = getPeriodStart(
          (period as IPeriodData).zoomMin! / 1000,
          newXZoom,
          curGraphDates.timezoneOffset,
        );
      }
      // check whether we got before the start of the data range
      const endGraphDates = getGraphBoundaryDates(
        rootState.trending.minTrendTimestamp,
        rootState.trending.maxTrendTimestamp,
        rootState.site.tzOffset,
        newXZoom,
      );
      if (newStartDate && endGraphDates.startDate && newStartDate < endGraphDates.startDate) {
        newStartDate = endGraphDates.startDate;
      }
      // make sure there is no end-date set if we switch from custom-date-period
      newEndDate = undefined;
    } else {
      // CUSTOM PERIOD-SELECT - use given start/end-time
      newStartDate = cmd.timestampStart;
      newEndDate = cmd.timestampEnd;
    }
  } else if (cmd.command === ActionGraphCommand.END) {
    if (newXZoom !== TimePeriod.CUSTOM) {
      newStartDate = undefined;
    } else {
      newEndDate = Math.round(new Date().getTime() / 1000);
    }
  } else if (cmd.command === ActionGraphCommand.START) {
    // which trend data is used as the bases to determine the start?
    const minTimestamp =
      identifier === "chargingSessionFilter"
        ? rootState.ocpp.minSessionTimestamp
        : identifier === "trendPeriod"
        ? rootState.trending.minTrendTimestamp
        : rootState.trending.minIoTimestamp;
    if (isSet(minTimestamp)) {
      newStartDate = getPeriodStart(minTimestamp, period.timePeriod, curGraphDates.timezoneOffset);
    }
  } else if (cmd.command === ActionGraphCommand.SET_DATE) {
    newStartDate = cmd.timestampStart;
    newEndDate = cmd.timestampEnd;
  } else {
    console.error("unexpected graph command", cmd);
  }
  // perform a final check whether the data are available in the given aggregation type
  const { deletionBoundary, aggregationBoundary } = getDeletionBoundary(rootState);
  // switch zoom? or adjust start date?
  if (identifier === "trendPeriod" && newStartDate) {
    // check deletion boundary
    if (isSet(deletionBoundary) && newStartDate < deletionBoundary.toSeconds()) {
      newStartDate = deletionBoundary.toSeconds();
    }
    if (
      newStartDate < aggregationBoundary.toSeconds() &&
      newXZoom &&
      [TimePeriod.DAY, TimePeriod.WEEK].includes(newXZoom)
    ) {
      newXZoom = TimePeriod.MONTH;
    }
  }
  return { newXZoom, newStartDate, newEndDate };
}

export default function guiReducer(
  state: IStateGui,
  action: IAction<unknown>,
  rootState: IState,
): IStateGui {
  switch (action.type) {
    case ActionType.ACT_NAVIGATION_COLLAPSE:
      // toggle navigation bar collapse status
      state.navigationIsOpen = !state.navigationIsOpen;
      return state;
    case ActionType.ACT_NAVIGATION_DROPDOWN_COLLAPSE: {
      const payload = action.payload as MainPanel;
      state.isDropdownOpen[payload] = !state.isDropdownOpen[payload];
      return state;
    }

    case ActionType.ACT_NAVIGATION: {
      const navigationArgs = action.payload as IStateRouter;
      const newPanel = navigationArgs.panel;

      // close potentially open drop-down navigation menu and confirm modal
      state.navigationIsOpen = false;
      state.confirmModal.isOpen = false;

      // reset zoom in dashboard
      if (newPanel === MainPanel.DASHBOARD) {
        state.trendPeriod.zoomMin = undefined;
        state.trendPeriod.zoomMax = undefined;
      }
      // reset charging session filter in showing all sessions
      if (newPanel === MainPanel.CHARGINGSESSIONLIST) {
        state.chargingSessionFilter.timePeriod = TimePeriod.MONTH;
      } else {
        // reset chargingSessionFilter - chargerName on any other panel than CHARGINGSESSIONLIST
        state.chargingSessionFilter.chargerName = undefined;
      }
      if (newPanel === MainPanel.CHARGERDETAIL) {
        // reset potential pending ocpp command state
        state.chargerCmd.state = OcppCmdState.NONE;
      }
      return state;
    }

    case ActionType.ACT_EDIT_VALUE: {
      const edit = action.payload as IActionTypeEdit;
      const editArray = Array.isArray(edit) ? edit : [edit];
      editArray.forEach((curItem) => {
        setInputField(state, curItem.name, curItem.value);
      });
      return state;
    }

    case ActionType.ACT_SELECT_SITE:
      // new site selected or just reloading cached site?
      if (isSet(action.payload)) {
        // remove data that shall not be persisted over potentially different sites
        state.rfidList = [];
        state.chargingSessionFilter = cloneDeep(INITIAL_STATE.chargingSessionFilter);
        state.reportLinks = [];
        state.selectedIoType = undefined;
        state.reportData = {
          requested: 0,
        };
      }
      return state;

    case ActionType.ACT_FILTER_CHARGERNAME: {
      const filter = action.payload as string;
      state.chargingSessionFilter.chargerName = filter;
      return state;
    }

    case ActionType.ACT_MODAL_CANCEL:
      state.modal.panel = ModalPanel.NONE;
      // ensure data update on next opening
      state.modalInput = undefined;
      state.modalOutput = undefined;
      state.modalReport = undefined;
      state.modalCharger = undefined;
      state.modalRfid = undefined;
      state.modalTrendData = undefined;
      return state;

    case ActionType.ACT_CONFIRM_POPUP: {
      const payload = action.payload as IActionTypeConfirmPopup;
      state.confirmModal = {
        ...payload,
        isOpen: true,
      };
      return state;
    }

    case ActionType.ACT_CONFIRM_CANCEL:
      state.confirmModal.isOpen = false;
      return state;

    case ActionType.ACT_ZOOM_GRAPH: {
      const data = action.payload as IActionTypeZoom;
      state[data.identifier].zoomMin = data.minX;
      state[data.identifier].zoomMax = data.maxX;
      return state;
    }

    case ActionType.ACT_CHANGE_PERIOD: {
      const cmd = action.payload as IActionTypeSetPeriod | undefined;
      if (isSet(cmd)) {
        const { newXZoom, newStartDate, newEndDate } = deriveNewGraphStartDate(
          state,
          rootState,
          cmd.identifier,
          cmd,
        );
        state[cmd.identifier].timePeriod = newXZoom;
        state[cmd.identifier].startDate = newStartDate;
        state[cmd.identifier].endDate = newEndDate;
        // reset zoom if we modify the graph
        if (isSet((state[cmd.identifier] as IPeriodData).zoomMin))
          (state[cmd.identifier] as IPeriodData).zoomMin = undefined;
        if (isSet((state[cmd.identifier] as IPeriodData).zoomMax))
          (state[cmd.identifier] as IPeriodData).zoomMax = undefined;
      }
      return state;
    }

    case ActionType.ACT_SELECT_TIMESTAMP:
      state.selectedTimestamp = action.payload as number;
      return state;

    case ActionType.ACT_EDIT_INPUT: {
      const id = action.payload as number;
      state.modal = {
        panel: ModalPanel.INPUT,
        dataState: DataState.EMPTY,
      };
      state.modalInput = id;
      return state;
    }
    case ActionType.ACT_CHANGE_INPUT_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_INPUT_SUCC:
      state.modal.dataState = DataState.SAVE_SUCC;
      if (state.modal.panel === ModalPanel.INPUT) {
        state.modal.panel = ModalPanel.NONE;
        state.modalInput = undefined;
      }
      return state;
    case ActionType.ACT_CHANGE_INPUT_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_EDIT_OUTPUT: {
      const id = action.payload as number;
      state.modal = {
        panel: ModalPanel.OUTPUT,
        dataState: DataState.EMPTY,
      };
      state.modalOutput = id;
      return state;
    }
    case ActionType.ACT_CHANGE_OUTPUT_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_OUTPUT_SUCC:
      state.modal.dataState = DataState.SAVE_SUCC;
      if (state.modal.panel === ModalPanel.OUTPUT) {
        state.modal.panel = ModalPanel.NONE;
        state.modalOutput = undefined;
      }
      return state;
    case ActionType.ACT_CHANGE_OUTPUT_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_EDIT_TRENDDATA:
      state.modal = {
        panel: ModalPanel.TREND,
        dataState: DataState.EMPTY,
      };
      state.modalTrendData = action.payload as IActionTrend;
      return state;
    case ActionType.ACT_CHANGE_TREND_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_TREND_SUCC:
      state.modal.dataState = DataState.SAVE_SUCC;
      if (state.modal.panel === ModalPanel.TREND) {
        state.modal.panel = ModalPanel.NONE;
        state.modalTrendData = undefined;
      }
      return state;
    case ActionType.ACT_CHANGE_TREND_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_GET_OCPP_SESSIONS_SUCC:
      // add new rfid cards to list
      (action.payload as IActionTypeChargingSessionList).forEach((curItem) => {
        if (curItem.rfid && !state.rfidList.includes(curItem.rfid)) {
          state.rfidList.push(curItem.rfid);
        }
      });
      return state;

    case ActionType.ACT_EDIT_REPORT:
      state.modal = {
        panel: ModalPanel.REPORT_SETTINGS,
        dataState: DataState.EMPTY,
      };
      state.modalReport = action.payload as number | undefined;
      return state;
    case ActionType.ACT_SAVE_REPORT_SETTINGS_SUCC:
      if (state.modal.panel === ModalPanel.REPORT_SETTINGS) {
        state.modal.panel = ModalPanel.NONE;
        state.modalReport = undefined;
      }
      return state;

    case ActionType.ACT_EDIT_TRENDMONITOR:
      state.modal = {
        panel: ModalPanel.TRENDMONITOR_SETTINGS,
        dataState: DataState.EMPTY,
      };
      state.modalTrendMonitor = action.payload as number | undefined;
      return state;
    case ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_SUCC:
      if (state.modal.panel === ModalPanel.TRENDMONITOR_SETTINGS) {
        state.modal.panel = ModalPanel.NONE;
        state.modalReport = undefined;
      }
      return state;

    case ActionType.ACT_CREATE_REPORT_SUCC:
      state.reportLinks[action.payload as number] = {
        requested: Date.now(),
      };
      return state;
    case ActionType.ACT_CREATE_REPORT_DATA_SUCC:
      state.reportData = {
        ...state.reportData!,
        requested: new Date().getTime(),
      };
      return state;
    case ActionType.ACT_NEW_REPORT: {
      const payload = action.payload as IActionNewReport;
      if (state.reportLinks[payload.reportId!]) {
        state.reportLinks[payload.reportId!]!.link = payload.link;
        state.reportLinks[payload.reportId!]!.error = payload.error;
      } else {
        console.warn("setting unknown report link", payload);
      }
      return state;
    }
    case ActionType.ACT_CREATE_REPORT_REQ: {
      state.reportData = {
        requested: new Date().getTime(),
        // make sure data and error is removed on a
        // new query, to show "Data loading..."
        data: undefined,
        error: undefined,
      };
      return state;
    }
    case ActionType.ACT_NEW_REPORT_DATA: {
      const payload = action.payload as IActionNewReportData;
      // ensure screen updates
      state.reportData = {
        ...state.reportData!,
        data: payload.data,
        error: payload.error,
      };

      return state;
    }
    case ActionType.ACT_CREATE_REPORT_FAIL:
      // remove potentially pending report generations
      for (let i = 0; i < state.reportLinks.length; i++) {
        if (isSet(state.reportLinks[i]) && !isSet(state.reportLinks[i]?.link)) {
          state.reportLinks[i] = null;
        }
      }
      // ensure screen updates
      state.reportLinks = [...state.reportLinks];
      state.reportData = {
        ...state.reportData!,
        data: undefined,
        error: (action.payload as string | undefined) ?? "Error generating report",
      };
      return state;

    case ActionType.ACT_GET_FILEUPLOADS_SUCC:
      state.fileUploads = action.payload as ImportFile[];
      return state;

    case ActionType.ACT_EDIT_CHARGER:
      state.modal = {
        panel: ModalPanel.CHARGER_SETTINGS,
        dataState: DataState.EMPTY,
      };
      state.modalCharger = action.payload as number | undefined;
      return state;
    case ActionType.ACT_SAVE_OCPP_CHARGERS_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_OCPP_CHARGERS_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      return state;
    case ActionType.ACT_SAVE_OCPP_CHARGERS_SUCC:
      state.modal.dataState = DataState.SAVE_SUCC;
      if (state.modal.panel === ModalPanel.CHARGER_SETTINGS) {
        state.modal.panel = ModalPanel.NONE;
        state.modalCharger = undefined;
      }
      return state;

    case ActionType.ACT_EDIT_RFID:
      state.modal = {
        panel: ModalPanel.RFID,
        dataState: DataState.EMPTY,
      };
      state.modalRfid = {
        id: typeof action.payload === "number" ? action.payload : undefined,
        addedrfid: typeof action.payload === "string" ? action.payload : undefined,
        checkResult: undefined,
      };
      return state;
    case ActionType.ACT_LINK_RFID:
      state.modal = {
        panel: ModalPanel.RFID_LINK,
        dataState: DataState.EMPTY,
      };
      state.modalRfid = {
        id: typeof action.payload === "number" ? action.payload : undefined,
        addedrfid: typeof action.payload === "string" ? action.payload : undefined,
        checkResult: undefined,
      };
      return state;
    case ActionType.ACT_SAVE_OCPP_RFID_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_OCPP_RFID_SUCC:
      if (state.modal.panel === ModalPanel.RFID || state.modal.panel === ModalPanel.RFID_LINK) {
        state.modal.panel = ModalPanel.NONE;
        state.modalRfid = undefined;
        state.modal.dataState = DataState.SAVE_SUCC;
      }
      return state;
    case ActionType.ACT_SAVE_OCPP_RFID_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      if (isSet(state.modalRfid)) {
        state.modalRfid.saveError = action.payload as string | undefined;
      }
      return state;

    case ActionType.ACT_EDIT_TARIFF:
      state.modal = {
        panel: ModalPanel.TARIFF,
        dataState: DataState.EMPTY,
      };
      state.modalTariff = action.payload as number | undefined;
      return state;
    case ActionType.ACT_SAVE_OCPP_TARIFF_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_OCPP_TARIFF_SUCC:
      // saving tariff settings
      if ([ModalPanel.TARIFF, ModalPanel.GLOBAL_TARIFF].includes(state.modal.panel)) {
        state.modal.panel = ModalPanel.NONE;
        state.modalTariff = undefined;
        state.modal.dataState = DataState.SAVE_SUCC;
      }
      // saving tariff association of rfid or charger
      if ([ModalPanel.RFID, ModalPanel.CHARGER_SETTINGS].includes(state.modal.panel)) {
        state.modal.dataState = DataState.SAVE_SUCC;
      }
      return state;
    case ActionType.ACT_SAVE_OCPP_TARIFF_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_NEW_GLOBAL_TARIFF:
      // open global tariff selection box
      state.modal = {
        panel: ModalPanel.GLOBAL_TARIFF,
        dataState: DataState.EMPTY,
      };
      return state;

    case ActionType.ACT_SORT_TABLE: {
      const payload = action.payload as IActionSortTable;
      state.tableSort[payload.table] = {
        column: payload.column,
        isSortAscending: payload.isSortAscending,
      };
      return state;
    }

    case ActionType.ACT_CHECK_OCPP_RFIDS_REQ:
      if (state.modalRfid) {
        state.modalRfid.checkResult = undefined;
        // remove save error indication if we modify the input
        if (state.modal.dataState === DataState.SAVE_FAIL) {
          state.modal.dataState = DataState.EMPTY;
        }
      }
      return state;
    case ActionType.ACT_CHECK_OCPP_RFIDS_SUCC:
      if (state.modalRfid) {
        state.modalRfid.checkResult = action.payload as string | undefined;
      }
      return state;
    case ActionType.ACT_CHECK_OCPP_RFIDS_FAIL:
      if (state.modalRfid) {
        state.modalRfid.checkResult = "";
      }
      return state;

    case ActionType.ACT_TRIGGER_CHARGER_CMD_REQ:
      state.chargerCmd.state = OcppCmdState.NEW;
      state.chargerCmd.hasError = false;
      state.chargerCmd.result = undefined;
      state.chargerCmd.timestampMs = (action.payload as IActionChargerCommand).timestampMs;
      state.chargerCmd.chargerId = (action.payload as IActionChargerCommand).chargerId;
      return state;
    case ActionType.ACT_TRIGGER_CHARGER_CMD_SUCC:
      state.chargerCmd.state = OcppCmdState.ISSUED;
      return state;
    case ActionType.ACT_TRIGGER_CHARGER_CMD_FAIL:
      state.chargerCmd.hasError = true;
      state.chargerCmd.result = action.payload as string;
      return state;
    case ActionType.ACT_NEW_OCPP_DATA: {
      const payload = action.payload as IActionNewChargerData;
      if (
        payload.chargerId === state.chargerCmd.chargerId &&
        payload.commandSeqNr === makeCommandSeqNr(state.chargerCmd.timestampMs)
      ) {
        // ocpp command result received
        // there may be race conditions between messages, so update only if we have no result yet
        if (!state.chargerCmd.hasError) state.chargerCmd.hasError = payload.commandHasError ?? true;
        if (!state.chargerCmd.result) state.chargerCmd.result = payload.commandResult;
        // state increment only if successful
        if (payload.commandHasError === false) {
          state.chargerCmd.state =
            !isSet(payload.commandResult) && state.chargerCmd.state !== OcppCmdState.CONFIRMED
              ? OcppCmdState.SENT
              : OcppCmdState.CONFIRMED;
        }
      }
      return state;
    }

    case ActionType.ACT_START_TRANSACTION_POPUP:
      state.modal = {
        panel: ModalPanel.START_TRANSACTION,
        dataState: DataState.EMPTY,
      };
      return state;

    case ActionType.ACT_PERFORM_FILE_UPLOAD_REQ:
      state.lastFileUploadError = undefined;
      return state;
    case ActionType.ACT_PERFORM_FILE_UPLOAD_FAIL:
    case ActionType.ACT_GET_FILEUPLOADS_FAIL:
      state.lastFileUploadError = (action.payload as string | undefined) ?? "Error uploading file";
      return state;

    case ActionType.ACT_LOGIN_REQ: // intentional fall-through
    case ActionType.ACT_SIGNUP_REQ: // intentional fall-through
    case ActionType.ACT_SIGNUP_CONFIRM_REQ: // intentional fall-through
    case ActionType.ACT_SIGNUP_RESEND_REQ: // intentional fall-through
    case ActionType.ACT_LOGOUT_REQ:
      state.login.userStateErrorInfo = undefined;
      state.login.userStateAWSFeedback = undefined;
      return state;

    case ActionType.ACT_LOGIN_FAIL:
      state.login.userStateErrorInfo = action.payload as string;
      return state;
    case ActionType.ACT_LOGIN_FAIL_AWS:
      state.login.userStateAWSFeedback = action.payload as UserStateAWSError;
      return state;
    case ActionType.ACT_LOGOUT_FAIL:
      state.login.userStateAWSFeedback = UserStateAWSError.LOGOUT_FAILED;
      return state;

    case ActionType.ACT_COGNITO_FAIL:
      state.login.userStateErrorInfo = action.payload as string;
      return state;
    case ActionType.ACT_COGNITO_FAIL_AWS:
      state.login.userStateAWSFeedback = action.payload as UserStateAWSError;
      return state;

    case ActionType.ACT_SIGNUP:
      state.login.email = action.payload as string | undefined;
      state.login.userStateAWSFeedback = undefined;
      state.login.userStateErrorInfo = undefined;
      return state;
    case ActionType.ACT_SIGNUP_CONFIRM: {
      const payload = action.payload as IActionTypeSignUp;
      state.login.email = payload.username;
      if (payload.keepErrorState === false) {
        state.login.userStateAWSFeedback = undefined;
        state.login.userStateErrorInfo = undefined;
      }
      return state;
    }

    case ActionType.ACT_BACK_TO_LOGIN: // intentional fall-through
    case ActionType.ACT_FORGOT_PASSWORD:
      state.login.email = action.payload as string;
      return state;

    case ActionType.ACT_FORGOT_PASSWORD_CODE_REQ: {
      const payload = action.payload as IActionTypeForgotPassword;
      state.login.email = payload.username;
      return state;
    }
    case ActionType.ACT_FORGOT_PASSWORD_CHANGE_REQ: {
      const payload = action.payload as IActionTypeLogin;
      state.login.email = payload.username;
      return state;
    }

    case ActionType.ACT_CHANGE_USER_FAIL:
      state.login.userErrorText = action.payload as string;
      return state;
    case ActionType.ACT_CHANGE_AWSUSER_FAIL:
      state.login.userStateErrorInfo = action.payload as string;
      return state;

    default:
      return state;
  }
}
